import {
	createFileRoute,
	Link,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { EditorContent } from "@tiptap/react";
import { useState } from "react";
import {
	graphql,
	useFragment,
	useLazyLoadQuery,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { Icon } from "@/components/icon";
import { LessonsTable } from "@/components/tables/lessons";
import { TextLink } from "@/components/text";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ReorderDrawer } from "@/dialogs/reorder-drawer";
import { SectionCreateDialog } from "@/dialogs/section-create-dialog";
import { useEditor } from "@/editor/editor";
import {
	CertificateForm,
	type CertificateFormValues,
} from "@/forms/certificate-form";
import { CourseForm, type CourseFormValues } from "@/forms/course-form";
import { useFileUpload } from "@/hooks/useFileUpload";
import { cn } from "@/utils";
import type { cursos_slugCertificateTab_course$key } from "./__generated__/cursos_slugCertificateTab_course.graphql";
import type { cursos_slugCertificateUpdateMutation } from "./__generated__/cursos_slugCertificateUpdateMutation.graphql";
import type { cursos_slugConfigTab_club$key } from "./__generated__/cursos_slugConfigTab_club.graphql";
import type { cursos_slugConfigTab_course$key } from "./__generated__/cursos_slugConfigTab_course.graphql";
import type { cursos_slugContentTab_course$key } from "./__generated__/cursos_slugContentTab_course.graphql";
import type { cursos_slugDeleteSectionMutation } from "./__generated__/cursos_slugDeleteSectionMutation.graphql";
import type { cursos_slugHeader_course$key } from "./__generated__/cursos_slugHeader_course.graphql";
import type { cursos_slugHiddenCommentsAlert_club$key } from "./__generated__/cursos_slugHiddenCommentsAlert_club.graphql";
import type { cursos_slugHiddenCommentsAlert_course$key } from "./__generated__/cursos_slugHiddenCommentsAlert_course.graphql";
import type { cursos_slugQuery } from "./__generated__/cursos_slugQuery.graphql";
import type { cursos_slugRevealCommentsMutation } from "./__generated__/cursos_slugRevealCommentsMutation.graphql";
import type { cursos_slugUpdateMutation } from "./__generated__/cursos_slugUpdateMutation.graphql";

const defaultValues = {
	view: "conteudo",
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$slug/",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z
				.enum(["conteudo", "configuracoes", "certificado"])
				.default("conteudo"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

type SectionDialogState = {
	open: boolean;
	section?: {
		id: string;
		name: string;
		sectionType: "REGULAR" | "BONUS" | "%future added value";
	};
};

function ContentTab({
	course,
	onSectionSaved,
}: {
	course: cursos_slugContentTab_course$key;
	onSectionSaved: () => void;
}) {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const navigate = useNavigate();
	const [deleteSection, isDeletingSection] =
		useMutation<cursos_slugDeleteSectionMutation>(graphql`
			mutation cursos_slugDeleteSectionMutation($id: ID!) {
				deleteCourseSection(id: $id) {
					id @deleteRecord
				}
			}
		`);
	const [sectionDialog, setSectionDialog] = useState<SectionDialogState>({
		open: false,
	});

	const data = useFragment(
		graphql`
			fragment cursos_slugContentTab_course on Course {
				id
				slug
				sections(status: ALL) {
					id
					name
					active
					sectionType
					position
					classes(status: ALL) {
						__typename
						id
						name
						slug
						active
						position
						completed
						canAccess
						... on CourseContent {
							video {
								__typename
								... on VideoFile {
									id
								}
								... on ExternalVideo {
									url
								}
							}
						}
					}
				}
			}
		`,
		course,
	);

	const sections =
		data.sections.map((section) => ({
			id: section.id,
			name: section.name,
			active: section.active,
			sectionType: section.sectionType as
				| "REGULAR"
				| "BONUS"
				| "%future added value",
			position: section.position ?? 0,
			lessons: section.classes.map((lesson) => ({
				id: lesson.id,
				name: lesson.name,
				slug: lesson.slug,
				active: lesson.active,
				position: lesson.position ?? 0,
				type: lesson.__typename as
					| "CourseContent"
					| "CourseQuiz"
					| "%future added value",
				videoType:
					lesson.__typename === "CourseContent" && lesson.video
						? (lesson.video.__typename as "VideoFile" | "ExternalVideo")
						: null,
				videoUrl:
					lesson.__typename === "CourseContent" &&
					lesson.video?.__typename === "ExternalVideo"
						? lesson.video.url
						: null,
			})),
		})) ?? [];

	const handleEditLesson = (
		_id: string,
		lessonSlug: string,
		type: "CourseContent" | "CourseQuiz" | "%future added value",
	) => {
		if (type === "CourseQuiz") {
			navigate({
				to: "/c/$club/area-de-membros/cursos/$slug/aulas/quiz/$lessonSlug",
				params: { club: club.slug, slug: data.slug, lessonSlug },
			});
		} else {
			navigate({
				to: "/c/$club/area-de-membros/cursos/$slug/aulas/$lessonSlug/conteudo",
				params: { club: club.slug, slug: data.slug, lessonSlug },
			});
		}
	};

	const handleCreateLesson = (sectionId: string) => {
		navigate({
			to: "/c/$club/area-de-membros/cursos/$slug/aulas/nova",
			params: { club: club.slug, slug: data.slug },
			search: { sectionId },
		});
	};

	const handleCreateQuiz = (sectionId: string) => {
		navigate({
			to: "/c/$club/area-de-membros/cursos/$slug/aulas/quiz/novo",
			params: { club: club.slug, slug: data.slug },
			search: { sectionId },
		});
	};

	const handleEditSection = (id: string) => {
		const section = sections.find((s) => s.id === id);
		if (section) {
			setSectionDialog({
				open: true,
				section: {
					id: section.id,
					name: section.name,
					sectionType: section.sectionType,
				},
			});
		}
	};

	const handleDeleteSection = (id: string) => {
		const section = sections.find((item) => item.id === id);
		if (!section || isDeletingSection) return;
		if (section.lessons.some((lesson) => lesson.active)) {
			toast.error("Não é possível excluir um módulo com aulas ativas.");
			return;
		}
		if (
			!window.confirm("Excluir este módulo? Esta ação não poderá ser desfeita.")
		) {
			return;
		}
		deleteSection({
			variables: { id },
			updater: (store) => {
				const courseRecord = store.get(data.id);
				const remaining = courseRecord
					?.getLinkedRecords("sections", { status: "ALL" })
					?.filter((record) => record?.getDataID() !== id);
				if (remaining) {
					courseRecord?.setLinkedRecords(remaining, "sections", {
						status: "ALL",
					});
				}
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Módulo excluído com sucesso!");
			},
			onError: () => {
				toast.error("Não foi possível excluir o módulo.");
			},
		});
	};

	const handleCreateSection = () => {
		setSectionDialog({ open: true });
	};

	const handleCloseSectionDialog = () => {
		setSectionDialog({ open: false });
	};

	return (
		<>
			<LessonsTable
				sections={sections}
				isPending={isDeletingSection}
				onEditLesson={handleEditLesson}
				onCreateLesson={handleCreateLesson}
				onCreateQuiz={handleCreateQuiz}
				onEditSection={handleEditSection}
				onDeleteSection={handleDeleteSection}
				onLessonDeleted={onSectionSaved}
			/>

			<div className="mt-6 flex justify-center">
				<Button variant="outline" onClick={handleCreateSection}>
					<Icon name="Plus" className="size-4" />
					Novo Módulo
				</Button>
			</div>

			<SectionCreateDialog
				club={club?.slug ?? ""}
				courseId={data.id}
				open={sectionDialog.open}
				onClose={handleCloseSectionDialog}
				onSaved={onSectionSaved}
				section={sectionDialog.section}
			/>
		</>
	);
}

function ConfiguracoesTab({
	course,
	club: clubFragment,
}: {
	course: cursos_slugConfigTab_course$key;
	club: cursos_slugConfigTab_club$key;
}) {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { session } = useRouteContext({ from: "__root__" });

	const [isSubmitting, setIsSubmitting] = useState(false);

	const courseData = useFragment(
		graphql`
			fragment cursos_slugConfigTab_course on Course {
				id
				name
				description
				visibility
				classAccessControl
				category {
					id
					orientation
				}
				landscapeImage {
					id
					signedUrl
				}
				portraitImage {
					id
					signedUrl
				}
				externalProducts {
					id
					productName
				}
				primaryProduct {
					id
				}
			}
		`,
		course,
	);

	const clubData = useFragment(
		graphql`
			fragment cursos_slugConfigTab_club on Club {
				subscription {
					features {
						key
						value
					}
				}
				offers(first: 100) {
					edges {
						node {
							id
							productName
							checkoutUrl
						}
					}
				}
				...upgradePlanCard_clubFragment
			}
		`,
		clubFragment,
	);

	const [updateCourse] = useMutation<cursos_slugUpdateMutation>(
		graphql`
			mutation cursos_slugUpdateMutation($payload: UpdateCourseInput!) {
				updateCourse(payload: $payload) {
					id
					name
					description
					visibility
					classAccessControl
					landscapeImage {
						id
						signedUrl
					}
					portraitImage {
						id
						signedUrl
					}
					externalProducts {
						id
						productName
					}
					primaryProduct {
						id
					}
				}
			}
		`,
	);

	const { upload } = useFileUpload({ club: club?.slug ?? "", session });

	const handleSubmit = async (values: CourseFormValues) => {
		setIsSubmitting(true);
		return new Promise<void>((resolve, reject) => {
			updateCourse({
				variables: {
					payload: {
						id: courseData.id,
						name: values.name,
						description: values.description,
						visibility: values.visibility,
						lessonAccessControl: values.classAccessControl,
						landscapeImageFile: values.landscapeImage?.id ?? null,
						portraitImageFile: values.portraitImage?.id ?? null,
						externalProducts: values.externalProducts.map((id) => ({ id })),
						primaryProduct: values.primaryProduct || null,
					},
				},
				onCompleted: (_response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						setIsSubmitting(false);
						reject(new Error(errors[0].message));
						return;
					}
					toast.success("Curso atualizado com sucesso!");
					setIsSubmitting(false);
					resolve();
				},
				onError: (error) => {
					toast.error("Erro ao atualizar curso. Tente novamente.");
					setIsSubmitting(false);
					reject(error);
				},
			});
		});
	};

	const secretVisibilityAllowed =
		courseData.visibility === "SECRET" ||
		clubData?.subscription?.features?.find(
			(feat) => feat.key === "secret_courses",
		)?.value === "1" ||
		false;

	const availableOffers =
		clubData.offers.edges.map((e) => ({
			id: e.node.id,
			productName: e.node.productName ?? null,
			checkoutUrl: e.node.checkoutUrl ?? null,
		})) ?? [];

	return (
		<CourseForm
			onSubmit={handleSubmit}
			defaultValues={{
				name: courseData.name,
				description: courseData.description,
				visibility: courseData.visibility as
					| "PUBLIC"
					| "PRIVATE"
					| "SECRET"
					| "MEMBER_ONLY",
				classAccessControl: courseData.classAccessControl as
					| "FLEXIBLE"
					| "PROGRESSIVE",
				landscapeImage: courseData.landscapeImage
					? {
							id: courseData.landscapeImage.id,
							url: courseData.landscapeImage.signedUrl,
						}
					: null,
				portraitImage: courseData.portraitImage
					? {
							id: courseData.portraitImage.id,
							url: courseData.portraitImage.signedUrl,
						}
					: null,
				externalProducts: courseData.externalProducts.map((p) => p.id),
				primaryProduct: courseData.primaryProduct?.id || null,
			}}
			offers={availableOffers}
			orientation={courseData.category.orientation as "LANDSCAPE" | "PORTRAIT"}
			onUpload={upload}
			club={club?.slug ?? ""}
			isSubmitting={isSubmitting}
			secretVisibilityAllowed={secretVisibilityAllowed}
			upgradePlanClub={clubData}
		/>
	);
}

function CertificadoTab({
	course,
}: {
	course: cursos_slugCertificateTab_course$key;
}) {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { session } = useRouteContext({ from: "__root__" });

	const [isSubmitting, setIsSubmitting] = useState(false);

	const courseData = useFragment(
		graphql`
			fragment cursos_slugCertificateTab_course on Course {
				id
				name
				certificateEmission
				certificateConfiguration {
					title
					courseName
					subtitle
					description
					showCourseWorkload
					showFinishDate
					showCertificateSignature
					logo {
						id
						signedUrl
					}
					backgroundImage {
						id
						signedUrl
					}
				}
			}
		`,
		course,
	);

	const [updateCourse] = useMutation<cursos_slugCertificateUpdateMutation>(
		graphql`
			mutation cursos_slugCertificateUpdateMutation($payload: UpdateCourseInput!) {
				updateCourse(payload: $payload) {
					id
					certificateEmission
					certificateConfiguration {
						title
						courseName
						subtitle
						description
						showCourseWorkload
						showFinishDate
						showCertificateSignature
						logo {
							id
							signedUrl
						}
						backgroundImage {
							id
							signedUrl
						}
					}
				}
			}
		`,
	);

	const { upload } = useFileUpload({ club: club?.slug ?? "", session });

	const config = courseData.certificateConfiguration;

	const handleSubmit = async (values: CertificateFormValues) => {
		setIsSubmitting(true);
		return new Promise<void>((resolve, reject) => {
			updateCourse({
				variables: {
					payload: {
						id: courseData.id,
						certificateEmission: values.certificateEmission,
						certificateConfiguration: {
							title: values.title,
							courseName: config?.courseName || courseData.name,
							subtitle: values.subtitle,
							description: values.description,
							showCourseWorkload: values.showCourseWorkload,
							showFinishDate: values.showFinishDate,
							showCertificateSignature: values.showCertificateSignature,
							logo: values.logo?.id ?? null,
							backgroundImageFile: values.backgroundImage?.id ?? null,
						},
					},
				},
				onCompleted: (_response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						setIsSubmitting(false);
						reject(new Error(errors[0].message));
						return;
					}
					toast.success("Certificado atualizado com sucesso!");
					setIsSubmitting(false);
					resolve();
				},
				onError: (error) => {
					toast.error("Erro ao atualizar certificado. Tente novamente.");
					setIsSubmitting(false);
					reject(error);
				},
			});
		});
	};

	return (
		<CertificateForm
			onSubmit={handleSubmit}
			defaultValues={{
				certificateEmission: courseData.certificateEmission ?? false,
				logo: config?.logo
					? { id: config.logo.id, url: config.logo.signedUrl }
					: null,
				backgroundImage: config?.backgroundImage
					? {
							id: config.backgroundImage.id,
							url: config.backgroundImage.signedUrl,
						}
					: null,
				title: config?.title ?? "",
				subtitle: config?.subtitle ?? "",
				description: config?.description ?? "",
				showCourseWorkload: config?.showCourseWorkload ?? true,
				showFinishDate: config?.showFinishDate ?? true,
				showCertificateSignature: config?.showCertificateSignature ?? true,
			}}
			onUpload={upload}
			isSubmitting={isSubmitting}
			courseName={courseData.name}
		/>
	);
}

function CourseHeader({ course }: { course: cursos_slugHeader_course$key }) {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });

	const courseData = useFragment(
		graphql`
			fragment cursos_slugHeader_course on Course {
				id
				name
				slug
				description
				url
				landscapeImage {
					id
					signedUrl
				}
				portraitImage {
					id
					signedUrl
				}
			}
		`,
		course,
	);

	const editor = useEditor({
		editable: false,
		content: courseData.description || undefined,
		club: club?.slug ?? "",
	});

	const handleViewAsStudent = () => {
		if (courseData.url) {
			window.open(courseData.url, "_blank");
		}
	};

	return (
		<div className="border rounded-md mb-6">
			<div
				className={cn(
					"p-6 flex flex-row items-start justify-between",
					editor?.isEmpty && "items-center",
				)}
			>
				<div className="flex-1">
					<h2 className="text-2xl font-semibold">{courseData.name}</h2>
					{editor && !editor.isEmpty && courseData.description && (
						<div className="text-muted-foreground mt-3">
							<EditorContent editor={editor} />
						</div>
					)}
				</div>
				<Button variant="outline" onClick={handleViewAsStudent}>
					<Icon name="Eye" />
					Ver como Aluno
				</Button>
			</div>
			{(courseData.landscapeImage || courseData.portraitImage) && (
				<div className="px-6 pb-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{courseData.landscapeImage && (
							<div className="flex flex-col gap-2">
								<span className="text-sm font-medium">Imagem Paisagem</span>
								<div className="rounded-md border overflow-hidden bg-muted/50">
									<img
										src={courseData.landscapeImage.signedUrl}
										alt="Landscape"
										className="w-full h-auto object-cover"
									/>
								</div>
							</div>
						)}
						{courseData.portraitImage && (
							<div className="flex flex-col gap-2">
								<span className="text-sm font-medium">Imagem Retrato</span>
								<div className="rounded-md border overflow-hidden bg-muted/50">
									<img
										src={courseData.portraitImage.signedUrl}
										alt="Portrait"
										className="w-full h-auto object-cover"
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

function HiddenCommentsAlert({
	course: courseFragment,
	club: clubFragment,
}: {
	course: cursos_slugHiddenCommentsAlert_course$key;
	club: cursos_slugHiddenCommentsAlert_club$key;
}) {
	const courseData = useFragment(
		graphql`
			fragment cursos_slugHiddenCommentsAlert_course on Course {
				id
				hasHiddenComments
			}
		`,
		courseFragment,
	);

	const clubData = useFragment(
		graphql`
			fragment cursos_slugHiddenCommentsAlert_club on Club {
				subscription {
					id
					features {
						key
						value
					}
				}
			}
		`,
		clubFragment,
	);

	const router = useRouter();

	const [revealComments, isRevealing] =
		useMutation<cursos_slugRevealCommentsMutation>(
			graphql`
				mutation cursos_slugRevealCommentsMutation($id: ID!) {
					revealCourseComments(id: $id) {
						id
						hasHiddenComments
					}
				}
			`,
		);

	const [upgradePlan, isUpgrading] = useMutation(graphql`
		mutation cursos_slugUpgradePlanMutation($subscriptionId: ID!, $plan: ID!) {
			clubSubscriptionUpdatePlan(plan: $plan, subscriptionId: $subscriptionId) {
				club {
					id
				}
			}
		}
	`);

	if (!courseData.hasHiddenComments) {
		return null;
	}

	const hasCommentImportFeature =
		clubData.subscription?.features?.find(
			(feat) => feat.key === "comment_import",
		)?.value === "1";

	if (!hasCommentImportFeature) {
		return (
			<div className="mb-6 flex flex-row items-center gap-3 bg-linear-to-br rounded from-emerald-600 via-emerald-700 to-[#036141] p-3">
				<div className="flex flex-col flex-1">
					<span className="font-semibold text-white text-base/6">
						Importar Comentários
					</span>
					<span className="text-sm/5 text-white">
						Identificamos que este curso possui comentários que não foram
						importados. Faça upgrade para o plano Pro para importá-los.{" "}
						<TextLink
							target="_blank"
							className="text-white underline-white"
							href="https://scarf.club/planos"
						>
							Saiba mais
						</TextLink>
					</span>
				</div>
				{clubData.subscription && (
					<Button
						className="w-fit h-fit flex flex-row items-center justify-center gap-2"
						variant="outline"
						disabled={isUpgrading}
						onClick={() =>
							upgradePlan({
								variables: {
									subscriptionId: clubData.subscription?.id,
									plan: "2",
								},
								onCompleted: (_response, errors) => {
									if (errors?.length) {
										toast.error(errors[0].message);
										return;
									}
									toast.success("Plano atualizado com sucesso!");
									router.invalidate();
								},
							})
						}
					>
						{isUpgrading && <Spinner />} Fazer Upgrade
					</Button>
				)}
			</div>
		);
	}

	return (
		<Alert className="mb-6">
			<AlertTitle>Importar Comentários</AlertTitle>
			<AlertDescription>
				Identificamos que este curso possui comentários que não foram
				importados. Deseja importá-los?
			</AlertDescription>

			<Button
				size="sm"
				className="w-fit mt-2"
				disabled={isRevealing}
				onClick={() =>
					revealComments({
						variables: { id: courseData.id },
						onCompleted: (_response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								return;
							}
							toast.success("Comentários importados com sucesso!");
						},
						onError: () => {
							toast.error("Erro ao importar comentários. Tente novamente.");
						},
					})
				}
			>
				Importar
				{isRevealing && <Spinner />}
			</Button>
		</Alert>
	);
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { slug } = Route.useParams();
	const { view } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const [isReorderDrawerOpen, setIsReorderDrawerOpen] = useState(false);
	const [courseFetchKey, setCourseFetchKey] = useState(0);

	const data = useLazyLoadQuery<cursos_slugQuery>(
		graphql`
			query cursos_slugQuery($clubSlug: String!, $courseSlug: String!) {
				club(slug: $clubSlug) {
					...cursos_slugConfigTab_club
					...cursos_slugHiddenCommentsAlert_club
					course(slug: $courseSlug) {
						...cursos_slugHeader_course
						...cursos_slugContentTab_course
						...cursos_slugConfigTab_course
						...cursos_slugCertificateTab_course
						...cursos_slugHiddenCommentsAlert_course
						...reorderDrawer_course
					}
				}
			}
		`,
		{ clubSlug: club?.slug ?? "", courseSlug: slug },
		{ fetchPolicy: "store-and-network", fetchKey: courseFetchKey },
	);

	return (
		<div className="p-6">
			<CourseHeader course={data.club.course} />
			<HiddenCommentsAlert course={data.club.course} club={data.club} />
			<Tabs
				defaultValue="conteudo"
				value={view}
				onValueChange={(val) =>
					navigate({
						search: (s) => ({
							...s,
							view: val as "conteudo" | "configuracoes" | "certificado",
						}),
					})
				}
				className="gap-6"
			>
				<div className="flex flex-col items-center justify-between gap-6 lg:flex-row">
					<TabsList>
						<TabsTrigger value="conteudo" className="border-none">
							Conteúdo
						</TabsTrigger>
						<TabsTrigger value="configuracoes" className="border-none">
							Configurações
						</TabsTrigger>
						<TabsTrigger value="certificado" className="border-none">
							Certificado
						</TabsTrigger>
					</TabsList>

					{view === "conteudo" ? (
						<div className="flex flex-row items-center justify-end gap-3">
							<Button variant="outline" size="sm" asChild>
								<Link
									to="/c/$club/area-de-membros/cursos/$slug/aulas/batch"
									params={{ club: club?.slug ?? "", slug }}
								>
									<Icon name="Upload" className="size-4" />
									Subir Aulas em Batch
								</Link>
							</Button>

							<Button variant="outline" size="sm" asChild>
								<a
									href={`/c/${club?.slug}/area-de-membros/cursos/${slug}/timeline`}
								>
									<Icon name="Clock9" className="size-4" />
									Liberação de Módulos
								</a>
							</Button>

							<Button
								variant="outline"
								size="sm"
								onClick={() => setIsReorderDrawerOpen(true)}
							>
								<Icon name="ArrowUpDown" className="size-4" />
								Reordenar
							</Button>
						</div>
					) : null}
				</div>
				<TabsContent value="conteudo">
					<ContentTab
						course={data.club.course}
						onSectionSaved={() => setCourseFetchKey((key) => key + 1)}
					/>
				</TabsContent>
				<TabsContent value="configuracoes">
					<ConfiguracoesTab course={data.club.course} club={data.club} />
				</TabsContent>
				<TabsContent value="certificado">
					<CertificadoTab course={data.club.course} />
				</TabsContent>
			</Tabs>

			<ReorderDrawer
				open={isReorderDrawerOpen}
				onOpenChange={setIsReorderDrawerOpen}
				course={data.club.course}
			/>
		</div>
	);
}
